import { useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '@/components/common/PageHeader';
import { Select2Field } from '@/components/forms/Select2Field';
import { TextField } from '@/components/forms/TextField';
import { DateRangePicker } from '@/components/forms/DateRangePicker';
import type { SelectOption } from '@/components/forms/SelectField';
import { useToast } from '@/components/common/toast';
import { ApiError } from '@/api/client/abpError';
import { tenantSetupApi } from '@/api/services/tenants';
import { useCountries, useCurrenciesWithSymbols } from '../api';
import { useTenantEnumOptions } from '../enumOptions';
import type { TenantContactDetailCreateDto, TenantSetupDto } from '@/api/types/tenants';

type Form = Record<string, string | boolean>;
const todayIso = () => new Date().toISOString().slice(0, 10);
const num = (v: unknown) => (v === '' || v == null ? 0 : Number(v));
const numOrNull = (v: unknown) => (v === '' || v == null ? null : Number(v));
const str = (v: unknown) => (v == null ? '' : String(v).trim());
const strOrNull = (v: unknown) => str(v) || null;

const STEPS = ['Client Info', 'Reports', 'Contact Details'];

export function TenantSetupWizard() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const {
    accountingFrameworkOptions,
    basisOfProvisionOptions,
    levelOfApplicationOptions,
    permanentCapitalRequirementOptions,
    productTypeOptions,
    prudentialClassificationOptions,
    reasonForFallingOptions,
    typeOfEntityOptions,
  } = useTenantEnumOptions();

  const countries = useCountries(id);
  const currencies = useCurrenciesWithSymbols(id);
  const countryOptions: SelectOption[] = useMemo(() => (countries.data ?? []).map((c) => ({ value: c.value, label: c.text })), [countries.data]);
  const currencyOptions: SelectOption[] = useMemo(() => (currencies.data ?? []).map((c) => ({ value: c.value, label: c.text })), [currencies.data]);

  const { register, control, handleSubmit } = useForm<Form>({
    defaultValues: { ti_mainIntegrationDate: todayIso(), ti_amlDate: todayIso() },
  });

  const contact = (vals: Form, prefix: string, contactFor: number): TenantContactDetailCreateDto => ({
    tenantId: id,
    contactFor,
    fullName: str(vals[`${prefix}_fullName`]),
    email: str(vals[`${prefix}_email`]),
    jobDescription: str(vals[`${prefix}_job`]),
    phone: str(vals[`${prefix}_phone`]),
  });
  const contactEmpty = (c: TenantContactDetailCreateDto) => !c.fullName && !c.email && !c.jobDescription && !c.phone;

  async function complete(vals: Form) {
    const person1 = contact(vals, 'p1', 1);
    if (contactEmpty(person1)) { toast.error('Contact Person 1 is required.'); setStep(2); return; }
    const person2 = contact(vals, 'p2', 2);
    const billing = contact(vals, 'bill', 3);
    const dto: TenantSetupDto = {
      tenantInformationCreateDto: {
        tenantId: id,
        typeOfEntity: num(vals.ti_typeOfEntity),
        maxCoverageOfFunds: num(vals.ti_maxCoverage),
        leiCode: strOrNull(vals.ti_lei),
        prudentialClassification: num(vals.ti_prudential),
        permanentCapitalRequirement: num(vals.ti_permanent),
        countryId: num(vals.ti_countryId),
        timeZone: strOrNull(vals.ti_timeZone),
        balanceHaircutPercentage: num(vals.ti_haircut),
        productType: num(vals.ti_productType),
        basisOfProvisionOfServices: num(vals.ti_basis),
      },
      tenantSettingCreateDto: {
        tenantId: id,
        reportingCurrencyId: num(vals.ti_reportingCurrency),
        ccyCurrencyId: num(vals.ti_ccyCurrency),
        instrumentProfitabilityIncludeLp: false,
      },
      tenantIntegrationDatesCreateDto: {
        tenantId: id,
        mainIntegrationDate: str(vals.ti_mainIntegrationDate),
        aMLSyncIntegrationDate: strOrNull(vals.ti_amlDate),
      },
      tenantReportsInformationCreateDto: {
        tenantId: id,
        trsIdentificationCodeOfEntity: strOrNull(vals.rep_trs),
        postalAddress: strOrNull(vals.rep_postal),
        faxNumber: strOrNull(vals.rep_fax),
        website: strOrNull(vals.rep_website),
        icfMemberName: strOrNull(vals.rep_icf),
        cifLicenseNumber: strOrNull(vals.rep_cif),
        address: strOrNull(vals.rep_address),
        street: strOrNull(vals.rep_street),
        building: strOrNull(vals.rep_building),
        district: strOrNull(vals.rep_district),
        city: strOrNull(vals.rep_city),
        tin: strOrNull(vals.rep_tin),
        inType: strOrNull(vals.rep_inType),
        reportingEntityCode: strOrNull(vals.rep_entityCode),
        accountingFramework: num(vals.rep_accounting),
        levelOfApplication: num(vals.rep_level),
        nameOfConsolidatingEntity: strOrNull(vals.rep_consolidating),
        reasonForFalling: num(vals.rep_reason),
        balanceSheetTotal: numOrNull(vals.rep_balanceSheet),
        combinedTotalAnnualGrossVenue: numOrNull(vals.rep_combinedGross),
        totalAnnualGrossVenue: numOrNull(vals.rep_totalGross),
        totalExpensesOfProfits: numOrNull(vals.rep_totalExpenses),
        ownCashDeposit: numOrNull(vals.rep_ownCash),
      },
      tenantContactDetailPerson1CreateDto: person1,
      tenantContactDetailPerson2CreateDto: contactEmpty(person2) ? null : person2,
      tenantContactDetailBillingCreateDto: contactEmpty(billing) ? null : billing,
    };
    setSaving(true);
    try {
      await tenantSetupApi.create(id, dto);
      toast.success('Setup successful!');
      navigate('/host/tenants');
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Setup failed.');
    } finally {
      setSaving(false);
    }
  }

  const next = () => setStep((s) => Math.min(2, s + 1));
  const prev = () => setStep((s) => Math.max(0, s - 1));

  const ContactCard = ({ prefix, title, required }: { prefix: string; title: string; required?: boolean }) => (
    <div className="card custom-card mb-2">
      <div className="card-header"><div className="card-title mb-0">{title}{required ? ' *' : ''}</div></div>
      <div className="card-body row g-3">
        <div className="col-lg-3"><TextField label="Full Name" {...register(`${prefix}_fullName`)} /></div>
        <div className="col-lg-3"><TextField label="Email" type="email" {...register(`${prefix}_email`)} /></div>
        <div className="col-lg-3"><TextField label="Phone" placeholder="(+000) 000 000 000" {...register(`${prefix}_phone`)} /></div>
        <div className="col-lg-3"><TextField label="Position" {...register(`${prefix}_job`)} /></div>
      </div>
    </div>
  );

  return (
    <div>
      <PageHeader title="Tenant Setup" breadcrumb={['Home', 'Tenants', 'Setup']} />

      <ul className="nav nav-pills nav-justified mb-3">
        {STEPS.map((s, i) => (
          <li className="nav-item" key={s}>
            <span className={`nav-link ${i === step ? 'active' : ''} ${i < step ? 'text-success' : ''}`}>{i + 1}. {s}</span>
          </li>
        ))}
      </ul>

      <form onSubmit={handleSubmit(complete)} noValidate>
        <div className={step === 0 ? '' : 'd-none'}>
          <div className="card custom-card"><div className="card-body row g-3">
            <div className="col-lg-4"><Select2Field control={control} name="ti_typeOfEntity" label="Type Of Entity" placeholder="Please select..." options={typeOfEntityOptions} /></div>
            <div className="col-lg-4"><TextField label="Max Coverage Of Funds" type="number" {...register('ti_maxCoverage')} /></div>
            <div className="col-lg-4"><TextField label="LEI Code" {...register('ti_lei')} /></div>
            <div className="col-lg-4"><Select2Field control={control} name="ti_prudential" label="Prudential Classification" options={prudentialClassificationOptions} /></div>
            <div className="col-lg-4"><Select2Field control={control} name="ti_permanent" label="Permanent Capital Requirement" options={permanentCapitalRequirementOptions} /></div>
            <div className="col-lg-4"><Select2Field control={control} name="ti_countryId" label="Country Of Incorporation" placeholder="Please select..." options={countryOptions} /></div>
            <div className="col-lg-4"><TextField label="Time Zone" placeholder="e.g. Europe/London" {...register('ti_timeZone')} /></div>
            <div className="col-lg-4"><Select2Field control={control} name="ti_reportingCurrency" label="Reporting Currency" placeholder="Please select..." options={currencyOptions} /></div>
            <div className="col-lg-4"><Select2Field control={control} name="ti_ccyCurrency" label="CCY Currency" placeholder="Please select..." options={currencyOptions} /></div>
            <div className="col-lg-4"><TextField label="Balance Haircut %" type="number" step="any" {...register('ti_haircut')} /></div>
            <div className="col-lg-4"><Select2Field control={control} name="ti_productType" label="Product Type" options={productTypeOptions} /></div>
            <div className="col-lg-4"><Select2Field control={control} name="ti_basis" label="Basis Of Provision Of Services" options={basisOfProvisionOptions} /></div>
            <div className="col-lg-4"><label className="form-label">Main Integration Date</label><Controller control={control} name="ti_mainIntegrationDate" render={({ field }) => <DateRangePicker mode="single" value={(field.value as string) || null} onChange={field.onChange} />} /></div>
            <div className="col-lg-4"><label className="form-label">AML Sync Integration Date</label><Controller control={control} name="ti_amlDate" render={({ field }) => <DateRangePicker mode="single" value={(field.value as string) || null} onChange={field.onChange} />} /></div>
          </div></div>
        </div>

        <div className={step === 1 ? '' : 'd-none'}>
          <div className="card custom-card"><div className="card-body row g-3">
            <div className="col-lg-4"><TextField label="TRS Identification Code Of Entity" {...register('rep_trs')} /></div>
            <div className="col-lg-4"><TextField label="Postal Address" {...register('rep_postal')} /></div>
            <div className="col-lg-4"><TextField label="Fax Number" {...register('rep_fax')} /></div>
            <div className="col-lg-4"><TextField label="Website" {...register('rep_website')} /></div>
            <div className="col-lg-4"><TextField label="ICF Member Name" {...register('rep_icf')} /></div>
            <div className="col-lg-4"><TextField label="CIF License Number" {...register('rep_cif')} /></div>
            <div className="col-lg-4"><TextField label="Address" {...register('rep_address')} /></div>
            <div className="col-lg-4"><TextField label="Street" {...register('rep_street')} /></div>
            <div className="col-lg-4"><TextField label="Building" {...register('rep_building')} /></div>
            <div className="col-lg-4"><TextField label="District" {...register('rep_district')} /></div>
            <div className="col-lg-4"><TextField label="City" {...register('rep_city')} /></div>
            <div className="col-lg-4"><TextField label="TIN" {...register('rep_tin')} /></div>
            <div className="col-lg-4"><TextField label="INType" {...register('rep_inType')} /></div>
            <div className="col-lg-4"><TextField label="Reporting Entity Code" maxLength={6} {...register('rep_entityCode')} /></div>
            <div className="col-lg-4"><Select2Field control={control} name="rep_accounting" label="Accounting Framework" options={accountingFrameworkOptions} /></div>
            <div className="col-lg-4"><Select2Field control={control} name="rep_level" label="Level Of Application" options={levelOfApplicationOptions} /></div>
            <div className="col-lg-4"><Select2Field control={control} name="rep_reason" label="Reason For Falling" options={reasonForFallingOptions} /></div>
            <div className="col-lg-4"><TextField label="Name of Consolidating Entity" {...register('rep_consolidating')} /></div>
            <div className="col-lg-4"><TextField label="Balance Sheet Total" type="number" step="any" {...register('rep_balanceSheet')} /></div>
            <div className="col-lg-4"><TextField label="Combined Total Annual Gross Revenue" type="number" step="any" {...register('rep_combinedGross')} /></div>
            <div className="col-lg-4"><TextField label="Total Annual Gross Revenue" type="number" step="any" {...register('rep_totalGross')} /></div>
            <div className="col-lg-4"><TextField label="Total Expenses Of Profits" type="number" step="any" {...register('rep_totalExpenses')} /></div>
            <div className="col-lg-4"><TextField label="Own Cash Deposit" type="number" step="any" {...register('rep_ownCash')} /></div>
          </div></div>
        </div>

        <div className={step === 2 ? '' : 'd-none'}>
          <ContactCard prefix="p1" title="Contact Details - Person 1" required />
          <ContactCard prefix="p2" title="Contact Details - Person 2" />
          <ContactCard prefix="bill" title="Billing Details" />
        </div>

        <div className="d-flex justify-content-between mt-3">
          <button type="button" className="btn btn-light" onClick={() => (step === 0 ? navigate('/host/tenants') : prev())}>
            {step === 0 ? 'Cancel' : 'Previous'}
          </button>
          {step < 2 ? (
            <button type="button" className="btn btn-primary" onClick={next}>Next</button>
          ) : (
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? <i className="fa fa-spinner fa-spin me-1" /> : null}Complete Setup
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
